import { faTelescope } from '@fortawesome/pro-duotone-svg-icons';
import React, { FC, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  EmptyState,
  EmptyStateContainer,
  EmptyStateIcon,
} from 'components/atoms/empty-state';
import { useHeaderHeight } from 'components/molecules/page';
import {
  TimesheetReportSummaryCell,
  TimesheetReportSummaryRow,
  TimesheetReportTable,
} from 'components/organisms/timesheets/common/report-table';

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

interface Props {
  columns;
  dataSource;
  summaryDataSource;
  pageHeaderHeight?: number;
  onRowClick?: (record) => void;
}

export const TimesheetTableWithSummary: FC<Props> = ({
  columns,
  dataSource,
  summaryDataSource,
  onRowClick,
}) => {
  const summaryColumns = columns.map((column) => ({
    ...column,
    render: column.summaryRender,
  }));

  useEffect(() => {
    const contentBody = document.querySelector(
      '.content-table .ant-table-body',
    );
    const summaryBody = document.querySelector(
      '.summary-table .ant-table-body',
    );

    const onScroll = (e) => {
      const source = e.target;
      const destination = e.target === contentBody ? summaryBody : contentBody;

      destination?.scroll({ left: source.scrollLeft });
    };

    contentBody?.addEventListener('scroll', onScroll);
    summaryBody?.addEventListener('scroll', onScroll);

    return () => {
      contentBody?.removeEventListener('scroll', onScroll);
      summaryBody?.removeEventListener('scroll', onScroll);
    };
  });

  const HEADER_HEIGHT = useHeaderHeight();
  const ACTION_BAR_HEIGHT = 48;
  const SUMMARY_WIDGET_HEIGHT = 104;
  const TAB_ROW_HEIGHT = 64;
  const TABLE_HEADER_HEIGHT = 48;
  const TABLE_BODY_HEIGHT =
    HEADER_HEIGHT +
    ACTION_BAR_HEIGHT +
    SUMMARY_WIDGET_HEIGHT +
    TAB_ROW_HEIGHT +
    TABLE_HEADER_HEIGHT;

  const EXTRA_SPACE_HEIGHT = 8;

  return (
    <>
      <TableWrapper>
        <TimesheetReportTable
          className="content-table"
          scroll={{
            y: `calc(100vh - ${TABLE_BODY_HEIGHT + EXTRA_SPACE_HEIGHT}px)`,
          }}
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          onRow={(record) => {
            return {
              onClick: () => {
                if (onRowClick) {
                  onRowClick(record);
                }
              },
            };
          }}
          summary={() =>
            dataSource.length === 0 ? (
              <></>
            ) : (
              <TimesheetReportSummaryRow>
                {summaryColumns.map((column, index) => (
                  <TimesheetReportSummaryCell
                    key={column.key}
                    index={index}
                    fixed={column.fixed != null}
                    className={
                      column.fixed != null
                        ? 'ant-table-cell-fix-left ant-table-cell-fix-left-last'
                        : ''
                    }
                  >
                    {summaryDataSource != null
                      ? column.render(
                          summaryDataSource[0][column.key],
                          summaryDataSource[0],
                        )
                      : null}
                  </TimesheetReportSummaryCell>
                ))}
              </TimesheetReportSummaryRow>
            )
          }
        />
      </TableWrapper>
      {dataSource.length === 0 ? (
        <EmptyStateContainer>
          <EmptyState
            image={<EmptyStateIcon icon={faTelescope} />}
            title={<FormattedMessage id="timesheets.reports.empty.title" />}
            message={<FormattedMessage id="timesheets.reports.empty.message" />}
          />
        </EmptyStateContainer>
      ) : null}
    </>
  );
};
